import React, { useCallback, useMemo, useRef, useState } from 'react';
import { IoCaretBack, IoCaretForward } from 'react-icons/io5';
import { PiPlant, PiTractor } from 'react-icons/pi';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { DatePicker, Tooltip } from 'antd';
import {
  FARMS_URL_PARAM_GROUP_BY,
  FARMS_URL_PARAM_NODE,
  FARMS_URL_PARAM_YEAR,
  INPUT_KEY_PREFIX,
} from 'constants/routeConstants';
import {
  TREE_GROUP_BY_CROP,
  TREE_GROUP_BY_FARM,
  TREE_GROUP_BY_VIEW_LABELS,
  TREE_YEAR_ALL,
  TREE_YEAR_ALL_LABEL,
  TREE_YEAR_ALL_SHORT_LABEL,
  TREE_YEAR_FILTER_ALL_YEARS_TEXT,
  TREE_YEAR_FILTER_ARIA_LABEL,
  TREE_YEAR_FILTER_TOOLTIP_TITLE,
  TREE_YEAR_NEXT_LABEL,
  TREE_YEAR_PREVIOUS_LABEL,
} from 'constants/uiConstants';
import dayjs from 'dayjs';
import { RootState } from 'redux/Store';
import type { TreeGroupBy } from 'utils/FarmUtils';
import MergeClassNames from 'utils/MergeClassNames';


// The 44px (h-11 w-11) hit target itself stays full-size for accessibility —
// only the visible fill is smaller, and only shown on hover, per feedback
// that a permanent fill was undesired.
const ARROW_CLASS =
  'group inline-flex h-11 w-11 items-center justify-center text-charcoal disabled:pointer-events-none disabled:opacity-40';
// ~30% smaller than the 44px hit target (2.75rem * 0.7 ≈ 1.925rem).
const ARROW_FILL_CLASS =
  'flex h-[1.925rem] w-[1.925rem] items-center justify-center rounded-[0.375rem] transition-colors group-hover:bg-gray-100 group-active:bg-gray-200';

const VIEW_ICON_BASE =
  'flex h-7 w-7 items-center justify-center rounded transition-colors';

const GROUP_BY_OPTIONS: TreeGroupBy[] = [
  TREE_GROUP_BY_FARM,
  TREE_GROUP_BY_CROP,
];

const VIEW_ICON_CONFIG: Record<
  TreeGroupBy,
  { label: string; Icon: React.ElementType; size: number }
> = {
  [TREE_GROUP_BY_FARM]: {
    label: TREE_GROUP_BY_VIEW_LABELS[TREE_GROUP_BY_FARM],
    Icon: PiTractor,
    size: 19,
  },
  [TREE_GROUP_BY_CROP]: {
    label: TREE_GROUP_BY_VIEW_LABELS[TREE_GROUP_BY_CROP],
    Icon: PiPlant,
    size: 19,
  },
};

const TreeViewControls: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const treeYearFilter = useSelector(
    (state: RootState) => state.farmReducer.treeYearFilter
  );
  const treeGroupBy = useSelector(
    (state: RootState) => state.farmReducer.treeGroupBy
  );

  // Clears node only when it has no equivalent in the new grouping mode.
  const navigateToGroupBy = useCallback(
    (value: TreeGroupBy) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === TREE_GROUP_BY_FARM) {
          next.delete(FARMS_URL_PARAM_GROUP_BY);
        } else {
          next.set(FARMS_URL_PARAM_GROUP_BY, value);
        }

        const currentNode = next.get(FARMS_URL_PARAM_NODE);
        if (currentNode && !currentNode.startsWith(INPUT_KEY_PREFIX)) {
          next.delete(FARMS_URL_PARAM_NODE);
        }

        return next;
      });
    },
    [setSearchParams]
  );

  // The picker is controlled so the "All years" footer action can close it.
  // `useDatePickerPanel` is deliberately not used here -- it owns onOpenChange.
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // rc-picker flushes a submit of its own panel value two frames after the
  // picker becomes both unfocused and closed (useRangeValue.js -- triggerSubmit
  // runs *before* syncWithValue, so it carries the stale year rather than the
  // cleared one). After choosing "All years" that submit would fire onChange
  // with the year that was showing and silently restore the filter. This flag
  // swallows exactly that one submit; it is cleared whenever the panel opens,
  // so a genuine selection is never suppressed.
  const ignoreNextChangeRef = useRef(false);

  const isAllYears = treeYearFilter === TREE_YEAR_ALL;

  // Stepping out of "All years" resumes from the current year, so the arrows
  // always have a concrete position to move from.
  const activeYear = isAllYears ? new Date().getFullYear() : treeYearFilter;

  // Memoised so the value keeps a stable identity across re-renders. rc-picker
  // resets the open panel when `value` changes identity, which is the panel-jump
  // regression OP-2285 fixed for the workspace date fields -- a new dayjs on
  // every render would reintroduce it here.
  const pickerValue = useMemo(
    () => (isAllYears ? null : dayjs().year(activeYear).startOf('year')),
    [isAllYears, activeYear]
  );

  const stepYear = useCallback(
    (offset: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(FARMS_URL_PARAM_YEAR, String(activeYear + offset));
        return next;
      });
    },
    [activeYear, setSearchParams]
  );

  // Preventing the default on mousedown is what actually defeats the stale
  // submit: focus never leaves the input, so rc-picker's `!focused && !open`
  // condition is never met and the flush never runs. The action itself stays on
  // click so the button is still reachable by keyboard, where Enter and Space
  // raise a click without any mousedown.
  const suppressBlur = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const showAllYears = useCallback(() => {
    ignoreNextChangeRef.current = true;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(FARMS_URL_PARAM_YEAR, TREE_YEAR_ALL);
      return next;
    });
    setIsPickerOpen(false);
  }, [setSearchParams]);

  const handleOpenChange = useCallback((open: boolean) => {
    // Opening the panel means any pending submit is stale, and the next change
    // will be a real selection -- never swallow that one.
    if (open) ignoreNextChangeRef.current = false;
    setIsPickerOpen(open);
  }, []);

  const handleChange = useCallback(
    (value: dayjs.Dayjs | null) => {
      if (ignoreNextChangeRef.current) {
        ignoreNextChangeRef.current = false;
        return;
      }
      if (value) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set(FARMS_URL_PARAM_YEAR, String(value.year()));
          return next;
        });
      }
    },
    [setSearchParams]
  );

  return (
    <div className="mb-1 flex items-center justify-between px-1">
      {/* Farm / Crop view icon toggles - top-left */}
      <div className="flex items-center gap-0.5">
        {GROUP_BY_OPTIONS.map((value) => {
          const { label, Icon, size } = VIEW_ICON_CONFIG[value];
          const isActive = treeGroupBy === value;
          return (
            <Tooltip key={value} title={label} placement="bottom">
              <button
                type="button"
                aria-label={label}
                className={MergeClassNames(
                  VIEW_ICON_BASE,
                  isActive
                    ? 'border border-b-4 border-success bg-white text-success'
                    : 'text-gray-muted hover:text-gray-dark'
                )}
                onClick={() => navigateToGroupBy(value)}
              >
                <Icon size={size} />
              </button>
            </Tooltip>
          );
        })}
      </div>

      {/* Year stepper - right side */}
      <div className="flex items-center">
        <Tooltip title={TREE_YEAR_PREVIOUS_LABEL}>
          <button
            type="button"
            aria-label={TREE_YEAR_PREVIOUS_LABEL}
            className={ARROW_CLASS}
            onClick={() => stepYear(-1)}
          >
            <span className={ARROW_FILL_CLASS}>
              <IoCaretBack size={18} />
            </span>
          </button>
        </Tooltip>

        {/*
         * The DatePicker is styled as a plain underlined link: no border, no
         * padding, no suffix icon. The year or "All" renders as clickable text;
         * clicking it (or the chevrons) opens the year-picker panel. Styling
         * targets antd's generated DOM directly via Tailwind arbitrary variants.
         * !important on padding is required because antd v5's hashed CSS-in-JS
         * rules share the same specificity as our override selector.
         */}
        <Tooltip
          title={
            <>
              <div>{TREE_YEAR_FILTER_TOOLTIP_TITLE}</div>
              <div>
                {isAllYears
                  ? TREE_YEAR_FILTER_ALL_YEARS_TEXT
                  : `Only showing ${activeYear} data`}
              </div>
            </>
          }
        >
          <DatePicker
            picker="year"
            aria-label={TREE_YEAR_FILTER_ARIA_LABEL}
            suffixIcon={null}
            variant="borderless"
            className={MergeClassNames(
              '[&.ant-picker]:!p-0',
              '[&.ant-picker]:!w-16',
              '[&_input]:!w-full',
              '[&_input]:cursor-pointer',
              '[&_input]:text-center',
              '[&_input]:text-base',
              '[&_input]:font-semibold',
              '[&_input]:!text-[color:var(--color-charcoal)]',
              '[&_input]:underline',
              '[&_input]:underline-offset-2',
              isAllYears &&
                '[&_input]:placeholder:!text-[color:var(--color-charcoal)]'
            )}
            open={isPickerOpen}
            onOpenChange={handleOpenChange}
            value={pickerValue}
            onChange={handleChange}
            allowClear={false}
            inputReadOnly
            placeholder={TREE_YEAR_ALL_SHORT_LABEL}
            renderExtraFooter={() => (
              <button
                type="button"
                className="w-full py-1 text-center text-sm text-success hover:underline"
                onMouseDown={suppressBlur}
                onClick={showAllYears}
              >
                {TREE_YEAR_ALL_LABEL}
              </button>
            )}
          />
        </Tooltip>

        <Tooltip title={TREE_YEAR_NEXT_LABEL}>
          <button
            type="button"
            aria-label={TREE_YEAR_NEXT_LABEL}
            className={ARROW_CLASS}
            onClick={() => stepYear(1)}
          >
            <span className={ARROW_FILL_CLASS}>
              <IoCaretForward size={18} />
            </span>
          </button>
        </Tooltip>
      </div>

    </div>
  );
};

export default TreeViewControls;
